import axios from 'axios';
import type {
  AuthTokens,
  User,
  Client,
  ClientFormData,
  ClientInteraction,
  ClientDocument,
  Service,
  ServiceCategory,
  ServiceImage,
  ServiceFeature,
  ServiceItem,
  ServiceItemFormData,
  ServiceItemHistory,
  PaginatedResponse,
  Proposal,
  ProposalFormData,
  ProposalItem,
  ProposalItemSelection,
  ProposalApproval,
  ItemSelectionAction,
  ItemSelectionStatus,
  Warehouse,
  WarehouseFormData,
  Vehicle,
  Holiday,
  HolidayFormData,
  ContaBancaria,
  CategoriaFinanceira,
  Beneficiario,
  CentroCusto,
  LancamentoFinanceiro,
  ContaPagar,
  ContaReceber,
  Fatura,
  FaturaItem,
  FormaPagamento,
  DashboardFinanceiroData,
  FluxoCaixaData,
  DREData,
  Material,
  MaterialFormData,
  MovimentacaoEstoque,
  MovimentacaoEstoqueFormData,
  NotaFiscal,
  ItemNotaFiscal,
  EstoqueDashboardData
} from '../types';

// Detectar automaticamente qual URL da API usar baseado em como o usuário está acessando
const getApiBaseUrl = (): string => {
  // Se há variável de ambiente configurada, usar ela
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const currentHost = window.location.hostname;
  const protocol = window.location.protocol; // http: ou https:

  // Se for um domínio (contém ponto e não é IP), usar o mesmo domínio
  // O Nginx vai interceptar /api/* e rotear para o backend
  if (currentHost.includes('.') && !currentHost.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return `${protocol}//${currentHost}`;
  }

  // Se for localhost ou IP, usar porta 8001 (ambiente de homologação)
  return `${protocol}//${currentHost}:8001`;
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request/response interceptors for debugging
api.interceptors.request.use((config) => {
  console.log('🚀 API Request:', {
    method: config.method?.toUpperCase(),
    url: config.url,
    baseURL: config.baseURL,
    fullURL: `${config.baseURL}${config.url}`,
    headers: config.headers,
    data: config.data
  });
  return config;
});

api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('❌ API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
      headers: error.response?.headers
    });

    // Se token inválido (401), fazer logout e redirecionar
    if (error.response?.status === 401) {
      console.log('🔐 Token inválido (401) - limpando tokens e redirecionando para login');
      localStorage.removeItem('authTokens');
      delete api.defaults.headers.common['Authorization'];

      // Só redireciona se não estiver já na página de login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Token management
let authTokens: AuthTokens | null = null;

export const setAuthTokens = (tokens: AuthTokens | null) => {
  authTokens = tokens;
  if (tokens) {
    api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
    // Salvar tokens com timestamp para validação de mudança de senha
    const tokenData = {
      ...tokens,
      timestamp: Date.now()
    };
    localStorage.setItem('authTokens', JSON.stringify(tokenData));
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('authTokens');
  }
};

// Inicializar tokens se existirem (mas validar no servidor)
try {
  const storedTokens = localStorage.getItem('authTokens');
  if (storedTokens) {
    const tokenData = JSON.parse(storedTokens);
    setAuthTokens({ access: tokenData.access, refresh: tokenData.refresh });
    console.log('🔐 Tokens restaurados - serão validados no servidor');
  }
} catch (error) {
  console.error('Erro ao restaurar tokens:', error);
  localStorage.removeItem('authTokens');
}

// Interceptor de resposta já está configurado acima (linhas 85-106)
// Removido duplicação

// Auth API
export const authApi = {
  login: async (username: string, password: string): Promise<AuthTokens> => {
    const response = await api.post('/api/v1/auth/token/', { username, password });
    return response.data;
  },

  refresh: async (refreshToken: string): Promise<{ access: string }> => {
    const response = await api.post('/api/v1/auth/token/refresh/', { refresh: refreshToken });
    return response.data;
  },

  verify: async (token: string): Promise<void> => {
    await api.post('/api/v1/auth/token/verify/', { token });
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/api/v1/auth/user/');
    return response.data;
  },

  // New authentication endpoints
  loginNew: async (username: string, password: string): Promise<{ user: User; tokens: AuthTokens; message: string }> => {
    const response = await api.post('/api/v1/auth/login/', { username, password });
    return response.data;
  },
  
  register: async (userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    password_confirm: string;
  }): Promise<{ user: User; tokens: AuthTokens; message: string }> => {
    const response = await api.post('/api/v1/auth/register/', userData);
    return response.data;
  },

  logout: async (refreshToken?: string): Promise<{ message: string }> => {
    const response = await api.post('/api/v1/auth/logout/', { refresh: refreshToken });
    return response.data;
  },

  updateProfile: async (userData: {
    first_name?: string;
    last_name?: string;
    email?: string;
  }): Promise<{ user: User; message: string }> => {
    const response = await api.put('/api/v1/auth/profile/', userData);
    return response.data;
  },

  changePassword: async (passwordData: {
    old_password: string;
    new_password: string;
  }): Promise<{ message: string }> => {
    const response = await api.post('/api/v1/auth/change-password/', passwordData);
    return response.data;
  },

  listUsers: async (): Promise<User[]> => {
    const response = await api.get('/api/v1/auth/users/');
    return response.data;
  },

  getUser: async (id: number): Promise<User> => {
    const response = await api.get(`/api/v1/auth/users/${id}/`);
    return response.data;
  },

  updateUser: async (id: number, userData: Partial<User>): Promise<User> => {
    const response = await api.put(`/api/v1/auth/users/${id}/update/`, userData);
    return response.data;
  },

  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/auth/users/${id}/delete/`);
  },

  createUser: async (userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    is_staff?: boolean;
    is_superuser?: boolean;
    is_active?: boolean;
  }): Promise<User> => {
    const response = await api.post('/api/v1/auth/users/create/', userData);
    return response.data;
  },
};

// Client API
export const clientApi = {
  getClients: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    person_type?: string;
    is_active?: boolean;
    city?: string;
    state?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<Client>> => {
    const response = await api.get('/api/v1/clients/clients/', { params });
    return response.data;
  },

  getClient: async (id: string): Promise<Client> => {
    const response = await api.get(`/api/v1/clients/clients/${id}/`);
    return response.data;
  },

  createClient: async (data: ClientFormData): Promise<Client> => {
    const response = await api.post('/api/v1/clients/clients/', data);
    return response.data;
  },

  updateClient: async (id: string, data: ClientFormData): Promise<Client> => {
    const response = await api.put(`/api/v1/clients/clients/${id}/`, data);
    return response.data;
  },

  deleteClient: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/clients/clients/${id}/`);
  },

  getClientsByDistance: async (lat: number, lng: number): Promise<Client[]> => {
    const response = await api.get('/api/v1/clients/clients/by_distance/', {
      params: { lat, lng },
    });
    return response.data;
  },

  addClientInteraction: async (
    clientId: string,
    data: Partial<ClientInteraction>
  ): Promise<ClientInteraction> => {
    const response = await api.post(`/api/v1/clients/clients/${clientId}/add_interaction/`, data);
    return response.data;
  },

  // Consultar CNPJ na Receita Federal
  consultarCNPJ: async (cnpj: string): Promise<{
    success: boolean;
    data: Partial<Client>;
    raw_data: any;
    from_cache: boolean;
  }> => {
    const cnpjLimpo = cnpj.replace(/[^\d]/g, '');
    const response = await api.get(`/api/v1/clients/cnpj-lookup/${cnpjLimpo}/`);
    return response.data;
  },
};

// Client Interaction API
export const interactionApi = {
  getInteractions: async (params?: {
    page?: number;
    client?: string;
    interaction_type?: string;
    responsible?: number;
    follow_up_completed?: boolean;
    ordering?: string;
  }): Promise<PaginatedResponse<ClientInteraction>> => {
    const response = await api.get('/api/v1/clients/interactions/', { params });
    return response.data;
  },

  getInteraction: async (id: string): Promise<ClientInteraction> => {
    const response = await api.get(`/api/v1/clients/interactions/${id}/`);
    return response.data;
  },

  createInteraction: async (data: Partial<ClientInteraction>): Promise<ClientInteraction> => {
    const response = await api.post('/api/v1/clients/interactions/', data);
    return response.data;
  },

  updateInteraction: async (id: string, data: Partial<ClientInteraction>): Promise<ClientInteraction> => {
    const response = await api.put(`/api/v1/clients/interactions/${id}/`, data);
    return response.data;
  },

  deleteInteraction: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/clients/interactions/${id}/`);
  },

  getPendingFollowup: async (): Promise<ClientInteraction[]> => {
    const response = await api.get('/api/v1/clients/interactions/pending_followup/');
    return response.data;
  },
};

// Client Document API
export const documentApi = {
  getDocuments: async (params?: {
    page?: number;
    client?: string;
    file_type?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<ClientDocument>> => {
    const response = await api.get('/api/v1/clients/documents/', { params });
    return response.data;
  },

  getDocument: async (id: string): Promise<ClientDocument> => {
    const response = await api.get(`/api/v1/clients/documents/${id}/`);
    return response.data;
  },

  uploadDocument: async (
    clientId: string,
    file: File,
    data: Partial<ClientDocument>
  ): Promise<ClientDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client', clientId);
    
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    const response = await api.post('/api/v1/clients/documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteDocument: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/clients/documents/${id}/`);
  },
};

// Catalog API
export const catalogApi = {
  // Service Categories
  getCategories: async (params?: {
    is_active?: boolean;
    search?: string;
    ordering?: string;
  }): Promise<ServiceCategory[]> => {
    const response = await api.get('/api/v1/catalog/categories/', { params });
    return response.data.results || response.data;
  },

  getCategory: async (id: string): Promise<ServiceCategory> => {
    const response = await api.get(`/api/v1/catalog/categories/${id}/`);
    return response.data;
  },

  createCategory: async (data: Partial<ServiceCategory>): Promise<ServiceCategory> => {
    const response = await api.post('/api/v1/catalog/categories/', data);
    return response.data;
  },

  updateCategory: async (id: string, data: Partial<ServiceCategory>): Promise<ServiceCategory> => {
    const response = await api.patch(`/api/v1/catalog/categories/${id}/`, data);
    return response.data;
  },

  deleteCategory: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/catalog/categories/${id}/`);
  },

  getCategoryServices: async (id: string, params?: {
    availability?: string;
    featured?: boolean;
  }): Promise<Service[]> => {
    const response = await api.get(`/api/v1/catalog/categories/${id}/services/`, { params });
    return response.data.results || response.data;
  },

  // Services
  getServices: async (params?: {
    page?: number;
    category?: string;
    category_slug?: string;
    availability_status?: string;
    featured?: boolean;
    is_active?: boolean;
    min_price?: number;
    max_price?: number;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<Service>> => {
    const response = await api.get('/api/v1/catalog/services/', { params });
    return response.data;
  },

  getService: async (id: string): Promise<Service> => {
    const response = await api.get(`/api/v1/catalog/services/${id}/`);
    return response.data;
  },

  createService: async (data: Partial<Service>): Promise<Service> => {
    const response = await api.post('/api/v1/catalog/services/', data);
    return response.data;
  },

  updateService: async (id: string, data: Partial<Service>): Promise<Service> => {
    const response = await api.put(`/api/v1/catalog/services/${id}/`, data);
    return response.data;
  },

  deleteService: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/catalog/services/${id}/`);
  },

  getFeaturedServices: async (): Promise<Service[]> => {
    const response = await api.get('/api/v1/catalog/services/featured/');
    return response.data.results || response.data;
  },

  getAvailableServices: async (): Promise<Service[]> => {
    const response = await api.get('/api/v1/catalog/services/available/');
    return response.data.results || response.data;
  },

  toggleServiceFeatured: async (id: string): Promise<{ featured: boolean; message: string }> => {
    const response = await api.post(`/api/v1/catalog/services/${id}/toggle_featured/`);
    return response.data;
  },

  changeServiceAvailability: async (id: string, availability_status: string): Promise<Service> => {
    const response = await api.post(`/api/v1/catalog/services/${id}/change_availability/`, { availability_status });
    return response.data;
  },

  // Service Images
  getServiceImages: async (params?: {
    service?: string;
    ordering?: string;
  }): Promise<ServiceImage[]> => {
    const response = await api.get('/api/v1/catalog/images/', { params });
    return response.data.results || response.data;
  },

  getServiceImage: async (id: string): Promise<ServiceImage> => {
    const response = await api.get(`/api/v1/catalog/images/${id}/`);
    return response.data;
  },

  createServiceImage: async (data: FormData): Promise<ServiceImage> => {
    const response = await api.post('/api/v1/catalog/images/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  updateServiceImage: async (id: string, data: Partial<ServiceImage>): Promise<ServiceImage> => {
    const response = await api.put(`/api/v1/catalog/images/${id}/`, data);
    return response.data;
  },

  deleteServiceImage: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/catalog/images/${id}/`);
  },

  setServiceImagePrimary: async (id: string): Promise<ServiceImage> => {
    const response = await api.post(`/api/v1/catalog/images/${id}/set_primary/`);
    return response.data;
  },

  // Service Features
  getServiceFeatures: async (params?: {
    service?: string;
    ordering?: string;
  }): Promise<ServiceFeature[]> => {
    const response = await api.get('/api/v1/catalog/features/', { params });
    return response.data.results || response.data;
  },

  getServiceFeature: async (id: string): Promise<ServiceFeature> => {
    const response = await api.get(`/api/v1/catalog/features/${id}/`);
    return response.data;
  },

  createServiceFeature: async (data: Partial<ServiceFeature>): Promise<ServiceFeature> => {
    const response = await api.post('/api/v1/catalog/features/', data);
    return response.data;
  },

  updateServiceFeature: async (id: string, data: Partial<ServiceFeature>): Promise<ServiceFeature> => {
    const response = await api.put(`/api/v1/catalog/features/${id}/`, data);
    return response.data;
  },

  deleteServiceFeature: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/catalog/features/${id}/`);
  },

  // Service Maintenance Items
  getServiceMaintenanceItems: async (serviceId: string): Promise<{
    service: Service;
    maintenance_items: ServiceItem[];
    count: number;
  }> => {
    const response = await api.get(`/api/v1/catalog/services/${serviceId}/maintenance_items/`);
    return response.data;
  },

  // Vehicles API (now using Services from vehicle category)
  getVehicles: async (params?: any): Promise<{ results: Service[]; count: number }> => {
    // Fetch services from vehicle category (is_vehicle_category=true)
    const response = await api.get('/api/v1/catalog/services/', {
      params: {
        ...params,
        category__is_vehicle_category: true
      }
    });

    // Filtrar apenas veículos com vehicle_type definido (para cálculo de pedágios)
    const filtered = response.data.results.filter((service: Service) =>
      service.vehicle_type && service.vehicle_type.trim() !== ''
    );

    return {
      results: filtered,
      count: filtered.length
    };
  },

  getVehicle: async (id: string): Promise<Service> => {
    const response = await api.get(`/api/v1/catalog/services/${id}/`);
    return response.data;
  },

  createVehicle: async (data: Partial<Service>): Promise<Service> => {
    const response = await api.post('/api/v1/catalog/services/', data);
    return response.data;
  },

  updateVehicle: async (id: string, data: Partial<Vehicle>): Promise<Vehicle> => {
    const response = await api.put(`/api/v1/catalog/vehicles/${id}/`, data);
    return response.data;
  },

  deleteVehicle: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/catalog/vehicles/${id}/`);
  },
};

// Proposals API
export const proposalsApi = {
  // Get all proposals with pagination and filters
  getProposals: async (params?: {
    page?: number;
    search?: string;
    status?: string;
    client?: string;
    responsible?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<PaginatedResponse<Proposal>> => {
    const response = await api.get('/api/v1/proposals/', { params });
    return response.data;
  },

  // Get single proposal by ID
  getProposal: async (id: string): Promise<Proposal> => {
    const response = await api.get(`/api/v1/proposals/${id}/`);
    return response.data;
  },

  // Create new proposal
  createProposal: async (data: ProposalFormData): Promise<Proposal> => {
    const response = await api.post('/api/v1/proposals/', data);
    return response.data;
  },

  // Update existing proposal
  updateProposal: async (id: string, data: Partial<ProposalFormData>): Promise<Proposal> => {
    const response = await api.put(`/api/v1/proposals/${id}/`, data);
    return response.data;
  },

  // Delete proposal
  deleteProposal: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/proposals/${id}/`);
  },

  // Update proposal status
  updateStatus: async (id: string, status: string, notes?: string): Promise<{ message: string; status: string }> => {
    const response = await api.post(`/api/v1/proposals/${id}/change_status/`, { status, notes });
    return response.data;
  },

  // Request approval for discount
  requestApproval: async (
    id: string,
    data: {
      discount_percentage: number;
      justification: string;
      approver: string;
    }
  ): Promise<{ message: string; approval_id: string }> => {
    const response = await api.post(`/api/v1/proposals/${id}/request_approval/`, data);
    return response.data;
  },

  // Respond to approval request
  respondApproval: async (
    id: string,
    data: {
      status: 'approved' | 'rejected';
      response_notes?: string;
    }
  ): Promise<{ message: string; proposal_status: string }> => {
    const response = await api.post(`/api/v1/proposals/${id}/respond_approval/`, data);
    return response.data;
  },

  // Create new version of proposal
  createVersion: async (
    id: string,
    data: {
      changes_description?: string;
      start_date?: string;
      end_date?: string;
      delivery_address?: string;
      delivery_city?: string;
      delivery_state?: string;
      delivery_zip_code?: string;
      distance_km?: number;
      discount_percentage?: number;
      internal_notes?: string;
      client_notes?: string;
    }
  ): Promise<Proposal> => {
    const response = await api.post(`/api/v1/proposals/${id}/create_version/`, data);
    return response.data;
  },

  // Get all versions of a proposal
  getVersions: async (id: string): Promise<Proposal[]> => {
    const response = await api.get(`/api/v1/proposals/${id}/versions/`);
    return response.data;
  },

  // Calculate preview of business rules and totals
  calculatePreview: async (id: string): Promise<{
    subtotal: number;
    business_rules: {
      minimum_cabins: number;
      transport_included: boolean;
      surcharge_percentage: number;
    };
    transport_surcharge_amount: number;
    discount_amount: number;
    total: number;
  }> => {
    const response = await api.get(`/api/v1/proposals/${id}/calculate_preview/`);
    return response.data;
  },
  
  // Generate PDF for proposal
  generatePdf: async (id: string): Promise<Blob> => {
    const response = await api.get(`/api/v1/proposals/${id}/generate_pdf/`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get contract details with allocated items
  getContractDetails: async (id: string): Promise<{
    proposal_number: string;
    client_name: string;
    status: string;
    total_value: number;
    rental_period: {
      start_date: string;
      end_date: string;
      days: number;
    };
    allocated_items: any[];
  }> => {
    const response = await api.get(`/api/v1/proposals/${id}/contract_details/`);
    return response.data;
  },

  // Get client proposal history
  getClientHistory: async (clientId: string): Promise<{
    client_id: string;
    statistics: {
      total_proposals: number;
      approved_proposals: number;
      signed_proposals: number;
      in_execution_proposals: number;
      success_rate: number;
    };
    proposals: Proposal[];
  }> => {
    const response = await api.get('/api/v1/proposals/client_history/', { 
      params: { client: clientId } 
    });
    return response.data;
  },

  // Negotiate proposal - specific endpoint for negotiation updates
  negotiateProposal: async (id: string, data: {
    items?: Array<{
      service: string;
      quantity: number;
      unit_price: number;
      custom_description?: string;
      notes?: string;
    }>;
    contractor_obligations?: string[];
    client_obligations?: string[];
    general_observations?: string[];
    discount_percentage?: number;
    status?: string;
    internal_notes?: string;
    client_notes?: string;
  }): Promise<Proposal> => {
    const response = await api.patch(`/api/v1/proposals/${id}/negotiate/`, data);
    return response.data;
  },

  // Proposal Items
  getProposalItems: async (proposalId: string): Promise<ProposalItem[]> => {
    const response = await api.get('/api/v1/proposal-items/', { params: { proposal: proposalId } });
    return response.data.results || response.data;
  },

  createProposalItem: async (data: Partial<ProposalItem>): Promise<ProposalItem> => {
    const response = await api.post('/api/v1/proposal-items/', data);
    return response.data;
  },

  updateProposalItem: async (id: string, data: Partial<ProposalItem>): Promise<ProposalItem> => {
    const response = await api.put(`/api/v1/proposal-items/${id}/`, data);
    return response.data;
  },

  patchProposalItem: async (id: string, data: Partial<ProposalItem>): Promise<ProposalItem> => {
    const response = await api.patch(`/api/v1/proposal-items/${id}/`, data);
    return response.data;
  },

  deleteProposalItem: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/proposal-items/${id}/`);
  },

  // Service Items API
  getServiceItems: async (params?: { service?: string, available_only?: boolean }): Promise<PaginatedResponse<ServiceItem>> => {
    const response = await api.get('/api/v1/catalog/items/', { params });
    return response.data;
  },

  getServiceItem: async (id: string): Promise<ServiceItem> => {
    const response = await api.get(`/api/v1/catalog/items/${id}/`);
    return response.data;
  },

  createServiceItem: async (data: ServiceItemFormData): Promise<ServiceItem> => {
    const response = await api.post('/api/v1/catalog/items/', data);
    return response.data;
  },

  updateServiceItem: async (id: string, data: Partial<ServiceItemFormData>): Promise<ServiceItem> => {
    const response = await api.put(`/api/v1/catalog/items/${id}/`, data);
    return response.data;
  },

  deleteServiceItem: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/catalog/items/${id}/`);
  },

  changeServiceItemStatus: async (id: string, status: string, description?: string): Promise<ServiceItem> => {
    const response = await api.post(`/api/v1/catalog/items/${id}/change_status/`, {
      status,
      description
    });
    return response.data;
  },

  changeServiceItemLocation: async (id: string, location: string, description?: string): Promise<ServiceItem> => {
    const response = await api.post(`/api/v1/catalog/items/${id}/change_location/`, {
      location,
      description
    });
    return response.data;
  },

  bulkCreateServiceItems: async (data: {
    service: string;
    quantity: number;
    prefix?: string;
    starting_number?: number;
    location?: string;
  }): Promise<{ message: string; items: ServiceItem[] }> => {
    const response = await api.post('/api/v1/catalog/items/bulk_create/', data);
    return response.data;
  },

  // Service Item History API
  getServiceItemHistory: async (item_id?: string): Promise<PaginatedResponse<ServiceItemHistory>> => {
    const params = item_id ? { item: item_id } : {};
    const response = await api.get('/api/v1/catalog/item-history/', { params });
    return response.data;
  },

  // Item Selection API (Sistema Híbrido C+B)
  selectItems: async (proposalItemId: string, action: ItemSelectionAction): Promise<{ 
    message: string; 
    selected_count: number 
  }> => {
    const response = await api.post(`/api/v1/proposal-items/${proposalItemId}/select_items/`, action);
    return response.data;
  },

  getAvailableItems: async (proposalItemId: string): Promise<ServiceItem[]> => {
    const response = await api.get(`/api/v1/proposal-items/${proposalItemId}/available_items/`);
    return response.data;
  },

  getSelectionStatus: async (proposalItemId: string): Promise<ItemSelectionStatus> => {
    const response = await api.get(`/api/v1/proposal-items/${proposalItemId}/selection_status/`);
    return response.data;
  },

  // Item Selections CRUD
  getItemSelections: async (proposal_id?: string): Promise<PaginatedResponse<ProposalItemSelection>> => {
    const params = proposal_id ? { proposal: proposal_id } : {};
    const response = await api.get('/api/v1/item-selections/', { params });
    return response.data;
  },

  createItemSelection: async (data: {
    proposal_item: string;
    service_item: string;
    selected_automatically?: boolean;
  }): Promise<ProposalItemSelection> => {
    const response = await api.post('/api/v1/item-selections/', data);
    return response.data;
  },

  deleteItemSelection: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/item-selections/${id}/`);
  },

  // Calculate distance for proposal item
  calculateItemDistance: async (itemId: string): Promise<{
    message: string;
    geocoded: boolean;
    origin: {
      warehouse: string;
      latitude: number;
      longitude: number;
    };
    destination: {
      latitude: number;
      longitude: number;
      address: string;
    };
    distance_km: number;
    duration_minutes: number;
    duration_text: string;
    route_polyline?: string;
    is_round_trip: boolean;
    vehicle?: any;
    costs?: {
      final_distance_km: number;
      logistics_cost: number;
      toll_cost: number;
      total_delivery_cost: number;
      currency: string;
    };
    tolls?: any[];
  }> => {
    const response = await api.post(`/api/v1/proposal-items/${itemId}/calculate_distance/`);
    return response.data;
  },

  // Calculate distance preview (sem salvar no banco)
  calculateDistancePreview: async (params: {
    service_id: number;
    latitude?: number;
    longitude?: number;
    address?: string;
    vehicle_id?: number;
    is_round_trip?: boolean;
  }): Promise<{
    message: string;
    geocoded: boolean;
    origin: {
      warehouse: string;
      latitude: number;
      longitude: number;
    };
    destination: {
      latitude: number;
      longitude: number;
      address: string;
    };
    distance_km: number;
    duration_minutes: number;
    duration_text: string;
    is_round_trip: boolean;
    vehicle?: any;
    costs?: {
      final_distance_km: number;
      logistics_cost: number;
      toll_cost: number;
      total_delivery_cost: number;
      currency: string;
    };
    tolls?: any[];
    toll_details?: any;
  }> => {
    const response = await api.post('/api/v1/proposals/calculate-distance/', params);
    return response.data;
  },
};

// Warehouse API
export const warehouseApi = {
  getWarehouses: async (params?: Record<string, any>): Promise<PaginatedResponse<Warehouse>> => {
    const response = await api.get('/api/v1/warehouses/', { params });
    return response.data;
  },

  getWarehouse: async (id: string): Promise<Warehouse> => {
    const response = await api.get(`/api/v1/warehouses/${id}/`);
    return response.data;
  },

  getMainWarehouse: async (): Promise<Warehouse> => {
    const response = await api.get('/api/v1/warehouses/main/');
    return response.data;
  },

  getActiveWarehouses: async (): Promise<Warehouse[]> => {
    const response = await api.get('/api/v1/warehouses/active/');
    return response.data;
  },

  getWarehousesWithCategories: async (categoryIds?: number[]): Promise<{
    warehouses: Array<{
      id: string;
      code: string;
      name: string;
      categories: Array<{
        category_id: number;
        category_name: string;
        category_slug: string;
        available: number;
        total: number;
      }>;
    }>;
    available_categories: Array<{
      id: number;
      name: string;
      slug: string;
    }>;
  }> => {
    const params = categoryIds && categoryIds.length > 0
      ? { categories: categoryIds.join(',') }
      : {};
    const response = await api.get('/api/v1/warehouses/with_categories/', { params });
    return response.data;
  },

  createWarehouse: async (data: WarehouseFormData): Promise<Warehouse> => {
    const response = await api.post('/api/v1/warehouses/', data);
    return response.data;
  },

  updateWarehouse: async (id: string, data: Partial<WarehouseFormData>): Promise<Warehouse> => {
    const response = await api.put(`/api/v1/warehouses/${id}/`, data);
    return response.data;
  },

  deleteWarehouse: async (id: string): Promise<void> => {
    await api.delete(`/api/v1/warehouses/${id}/`);
  },
};

// Service Schedule API
export const scheduleApi = {
  getSchedules: async (params?: {
    proposal?: string;
    proposal_item?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    pending_only?: boolean;
  }): Promise<any[]> => {
    const response = await api.get('/api/v1/proposals/service-schedule/', { params });
    return response.data.results || response.data;
  },

  generateScheduleForItem: async (proposalItemId: string): Promise<{ detail: string; count: number }> => {
    const response = await api.post('/api/v1/proposals/service-schedule/generate-for-item/', {
      proposal_item_id: proposalItemId
    });
    return response.data;
  },

  markAsCompleted: async (scheduleId: string, executedDate?: string, notes?: string): Promise<any> => {
    const response = await api.post(`/api/v1/proposals/service-schedule/${scheduleId}/execute_action/`, {
      action: 'mark_completed',
      executed_date: executedDate,
      notes: notes
    });
    return response.data;
  },

  reschedule: async (scheduleId: string, newDate: string, reason?: string): Promise<any> => {
    const response = await api.post(`/api/v1/proposals/service-schedule/${scheduleId}/execute_action/`, {
      action: 'reschedule',
      new_date: newDate,
      reason: reason
    });
    return response.data;
  },

  cancel: async (scheduleId: string, reason?: string): Promise<any> => {
    const response = await api.post(`/api/v1/proposals/service-schedule/${scheduleId}/execute_action/`, {
      action: 'cancel',
      reason: reason
    });
    return response.data;
  }
};

// Holiday API
export const holidayApi = {
  getHolidays: async (params?: {
    tipo?: string;
    estado?: string;
    cidade?: string;
    ativo?: boolean;
    ano?: number;
  }): Promise<Holiday[]> => {
    const response = await api.get('/api/v1/feriados/', { params });
    // Handle both paginated and non-paginated responses
    return Array.isArray(response.data) ? response.data : (response.data.results || []);
  },

  getHoliday: async (id: number): Promise<Holiday> => {
    const response = await api.get(`/api/v1/feriados/${id}/`);
    return response.data;
  },

  createHoliday: async (data: HolidayFormData): Promise<Holiday> => {
    const response = await api.post('/api/v1/feriados/', data);
    return response.data;
  },

  updateHoliday: async (id: number, data: Partial<HolidayFormData>): Promise<Holiday> => {
    const response = await api.put(`/api/v1/feriados/${id}/`, data);
    return response.data;
  },

  deleteHoliday: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/feriados/${id}/`);
  },

  importHolidays: async (ano: number, estado?: string): Promise<{ success: boolean; importados: number; atualizados: number; erros: string[] }> => {
    const payload: any = { ano };
    if (estado) {
      payload.estado = estado;
    }
    const response = await api.post('/api/v1/feriados/import/', payload);
    return response.data;
  },

  toggleActive: async (id: number): Promise<Holiday> => {
    const holiday = await holidayApi.getHoliday(id);
    return await holidayApi.updateHoliday(id, { ativo: !holiday.ativo });
  }
};

// Financeiro API
export const financeiroApi = {
  // Dashboard
  getDashboard: async (): Promise<DashboardFinanceiroData> => {
    const response = await api.get('/api/v1/financeiro/dashboard/');
    return response.data;
  },

  // Fluxo de Caixa
  getFluxoCaixa: async (dias: number = 30): Promise<FluxoCaixaData> => {
    const response = await api.get('/api/v1/financeiro/fluxo-caixa/', { params: { dias } });
    return response.data;
  },

  // DRE
  getDRE: async (data_inicio: string, data_fim: string): Promise<DREData> => {
    const response = await api.get('/api/v1/financeiro/dre/', {
      params: { data_inicio, data_fim }
    });
    return response.data;
  },

  // Relatório de Movimentações
  getRelatorioMovimentacoes: async (data_inicio: string, data_fim: string): Promise<any> => {
    const response = await api.get('/api/v1/financeiro/relatorios/movimentacoes/', {
      params: { data_inicio, data_fim }
    });
    return response.data;
  },

  // Contas Bancárias
  getContas: async (params?: Record<string, any>): Promise<PaginatedResponse<ContaBancaria>> => {
    const response = await api.get('/api/v1/financeiro/contas/', { params });
    return response.data;
  },

  getConta: async (id: number): Promise<ContaBancaria> => {
    const response = await api.get(`/api/v1/financeiro/contas/${id}/`);
    return response.data;
  },

  createConta: async (data: Partial<ContaBancaria>): Promise<ContaBancaria> => {
    const response = await api.post('/api/v1/financeiro/contas/', data);
    return response.data;
  },

  updateConta: async (id: number, data: Partial<ContaBancaria>): Promise<ContaBancaria> => {
    const response = await api.put(`/api/v1/financeiro/contas/${id}/`, data);
    return response.data;
  },

  deleteConta: async (id: string | number): Promise<void> => {
    await api.delete(`/api/v1/financeiro/contas/${id}/`);
  },

  // Categorias
  getCategorias: async (params?: Record<string, any>): Promise<PaginatedResponse<CategoriaFinanceira>> => {
    const response = await api.get('/api/v1/financeiro/categorias/', { params });
    return response.data;
  },

  getCategoria: async (id: string | number): Promise<CategoriaFinanceira> => {
    const response = await api.get(`/api/v1/financeiro/categorias/${id}/`);
    return response.data;
  },

  getCategoriaPorTipo: async (): Promise<{
    receitas: CategoriaFinanceira[];
    despesas: CategoriaFinanceira[];
    transferencias: CategoriaFinanceira[];
  }> => {
    const response = await api.get('/api/v1/financeiro/categorias/por_tipo/');
    return response.data;
  },

  createCategoria: async (data: Partial<CategoriaFinanceira>): Promise<CategoriaFinanceira> => {
    const response = await api.post('/api/v1/financeiro/categorias/', data);
    return response.data;
  },

  updateCategoria: async (id: number, data: Partial<CategoriaFinanceira>): Promise<CategoriaFinanceira> => {
    const response = await api.put(`/api/v1/financeiro/categorias/${id}/`, data);
    return response.data;
  },

  deleteCategoria: async (id: string | number): Promise<void> => {
    await api.delete(`/api/v1/financeiro/categorias/${id}/`);
  },

  // Beneficiários (usa API de Beneficiarios do financeiro)
  getBeneficiarios: async (params?: Record<string, any>): Promise<PaginatedResponse<Client>> => {
    const response = await api.get('/api/v1/financeiro/beneficiarios/', { params });
    return response.data;
  },

  createBeneficiario: async (data: Partial<Client>): Promise<Client> => {
    const response = await api.post('/api/v1/financeiro/beneficiarios/', data);
    return response.data;
  },

  updateBeneficiario: async (id: number, data: Partial<Client>): Promise<Client> => {
    const response = await api.put(`/api/v1/financeiro/beneficiarios/${id}/`, data);
    return response.data;
  },

  deleteBeneficiario: async (id: string | number): Promise<void> => {
    await api.delete(`/api/v1/financeiro/beneficiarios/${id}/`);
  },

  // Centros de Custo
  getCentrosCusto: async (params?: Record<string, any>): Promise<PaginatedResponse<CentroCusto>> => {
    const response = await api.get('/api/v1/financeiro/centros-custo/', { params });
    return response.data;
  },

  // Lançamentos
  getLancamentos: async (params?: Record<string, any>): Promise<PaginatedResponse<LancamentoFinanceiro>> => {
    const response = await api.get('/api/v1/financeiro/lancamentos/', { params });
    return response.data;
  },

  getLancamento: async (id: number): Promise<LancamentoFinanceiro> => {
    const response = await api.get(`/api/v1/financeiro/lancamentos/${id}/`);
    return response.data;
  },

  createLancamento: async (data: Omit<LancamentoFinanceiro, 'id' | 'created_at' | 'updated_at'>): Promise<LancamentoFinanceiro> => {
    const response = await api.post('/api/v1/financeiro/lancamentos/', data);
    return response.data;
  },

  updateLancamento: async (id: number, data: Partial<LancamentoFinanceiro>): Promise<LancamentoFinanceiro> => {
    const response = await api.put(`/api/v1/financeiro/lancamentos/${id}/`, data);
    return response.data;
  },

  deleteLancamento: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/financeiro/lancamentos/${id}/`);
  },

  confirmarLancamento: async (id: number, data: {
    data_pagamento: string;
    valor_pago?: number;
    forma_pagamento?: FormaPagamento;
  }): Promise<LancamentoFinanceiro> => {
    const response = await api.post(`/api/v1/financeiro/lancamentos/${id}/confirmar/`, data);
    return response.data;
  },

  cancelarLancamento: async (id: number): Promise<LancamentoFinanceiro> => {
    const response = await api.post(`/api/v1/financeiro/lancamentos/${id}/cancelar/`);
    return response.data;
  },

  getLancamentosPendentes: async (): Promise<PaginatedResponse<LancamentoFinanceiro>> => {
    const response = await api.get('/api/v1/financeiro/lancamentos/pendentes/');
    return response.data;
  },

  getLancamentosVencidos: async (): Promise<PaginatedResponse<LancamentoFinanceiro>> => {
    const response = await api.get('/api/v1/financeiro/lancamentos/vencidos/');
    return response.data;
  },

  getLancamentosVencendoHoje: async (): Promise<LancamentoFinanceiro[]> => {
    const response = await api.get('/api/v1/financeiro/lancamentos/vencendo_hoje/');
    return response.data;
  },

  // ===== CONTAS A PAGAR =====
  getContasPagar: async (params?: Record<string, any>): Promise<PaginatedResponse<ContaPagar>> => {
    const response = await api.get('/api/v1/financeiro/contas-pagar/', { params });
    return response.data;
  },

  getContaPagar: async (id: number): Promise<ContaPagar> => {
    const response = await api.get(`/api/v1/financeiro/contas-pagar/${id}/`);
    return response.data;
  },

  createContaPagar: async (data: Partial<ContaPagar>): Promise<ContaPagar> => {
    const response = await api.post('/api/v1/financeiro/contas-pagar/', data);
    return response.data;
  },

  updateContaPagar: async (id: number, data: Partial<ContaPagar>): Promise<ContaPagar> => {
    const response = await api.put(`/api/v1/financeiro/contas-pagar/${id}/`, data);
    return response.data;
  },

  deleteContaPagar: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/financeiro/contas-pagar/${id}/`);
  },

  confirmarPagamentoContaPagar: async (id: number, data: {
    data_pagamento: string;
    valor_pago?: number;
    forma_pagamento?: FormaPagamento;
  }): Promise<ContaPagar> => {
    const response = await api.post(`/api/v1/financeiro/contas-pagar/${id}/confirmar_pagamento/`, data);
    return response.data;
  },

  cancelarContaPagar: async (id: number): Promise<ContaPagar> => {
    const response = await api.post(`/api/v1/financeiro/contas-pagar/${id}/cancelar/`);
    return response.data;
  },

  parseNFePagarXML: async (xmlFile: File): Promise<any> => {
    const formData = new FormData();
    formData.append('xml_file', xmlFile);

    const response = await api.post('/api/v1/financeiro/contas-pagar/parse_nfe_xml/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getContasPagarPendentes: async (): Promise<PaginatedResponse<ContaPagar>> => {
    const response = await api.get('/api/v1/financeiro/contas-pagar/pendentes/');
    return response.data;
  },

  getContasPagarVencidas: async (): Promise<PaginatedResponse<ContaPagar>> => {
    const response = await api.get('/api/v1/financeiro/contas-pagar/vencidas/');
    return response.data;
  },

  // ===== CONTAS A RECEBER =====
  getContasReceber: async (params?: Record<string, any>): Promise<PaginatedResponse<ContaReceber>> => {
    const response = await api.get('/api/v1/financeiro/contas-receber/', { params });
    return response.data;
  },

  getContaReceber: async (id: number): Promise<ContaReceber> => {
    const response = await api.get(`/api/v1/financeiro/contas-receber/${id}/`);
    return response.data;
  },

  createContaReceber: async (data: Partial<ContaReceber>): Promise<ContaReceber> => {
    const response = await api.post('/api/v1/financeiro/contas-receber/', data);
    return response.data;
  },

  updateContaReceber: async (id: number, data: Partial<ContaReceber>): Promise<ContaReceber> => {
    const response = await api.put(`/api/v1/financeiro/contas-receber/${id}/`, data);
    return response.data;
  },

  deleteContaReceber: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/financeiro/contas-receber/${id}/`);
  },

  confirmarRecebimentoContaReceber: async (id: number, data: {
    data_pagamento: string;
    valor_pago?: number;
    forma_pagamento?: FormaPagamento;
  }): Promise<ContaReceber> => {
    const response = await api.post(`/api/v1/financeiro/contas-receber/${id}/confirmar_recebimento/`, data);
    return response.data;
  },

  cancelarContaReceber: async (id: number): Promise<ContaReceber> => {
    const response = await api.post(`/api/v1/financeiro/contas-receber/${id}/cancelar/`);
    return response.data;
  },

  parseNFeXML: async (xmlFile: File): Promise<any> => {
    const formData = new FormData();
    formData.append('xml_file', xmlFile);

    const response = await api.post('/api/v1/financeiro/contas-receber/parse_nfe_xml/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  protestarContaReceber: async (id: number, data_protesto: string): Promise<ContaReceber> => {
    const response = await api.post(`/api/v1/financeiro/contas-receber/${id}/protestar/`, { data_protesto });
    return response.data;
  },

  negativarContaReceber: async (id: number, data_negativacao: string): Promise<ContaReceber> => {
    const response = await api.post(`/api/v1/financeiro/contas-receber/${id}/negativar/`, { data_negativacao });
    return response.data;
  },

  registrarCobrancaContaReceber: async (id: number, tipo_cobranca: string): Promise<ContaReceber> => {
    const response = await api.post(`/api/v1/financeiro/contas-receber/${id}/registrar_cobranca/`, { tipo_cobranca });
    return response.data;
  },

  getContasReceberPendentes: async (): Promise<PaginatedResponse<ContaReceber>> => {
    const response = await api.get('/api/v1/financeiro/contas-receber/pendentes/');
    return response.data;
  },

  getContasReceberVencidas: async (): Promise<PaginatedResponse<ContaReceber>> => {
    const response = await api.get('/api/v1/financeiro/contas-receber/vencidas/');
    return response.data;
  },

  getContasReceberProtestadas: async (): Promise<PaginatedResponse<ContaReceber>> => {
    const response = await api.get('/api/v1/financeiro/contas-receber/protestadas/');
    return response.data;
  },

  getContasReceberNegativadas: async (): Promise<PaginatedResponse<ContaReceber>> => {
    const response = await api.get('/api/v1/financeiro/contas-receber/negativadas/');
    return response.data;
  },

  // Subcategorias
  getSubcategorias: async (params?: Record<string, any>): Promise<PaginatedResponse<any>> => {
    const response = await api.get('/api/v1/financeiro/subcategorias/', { params });
    return response.data;
  },

  createSubcategoria: async (data: { nome: string; ativa?: boolean }): Promise<any> => {
    const response = await api.post('/api/v1/financeiro/subcategorias/', data);
    return response.data;
  },

  patchCategoria: async (id: number, data: Partial<CategoriaFinanceira>): Promise<CategoriaFinanceira> => {
    const response = await api.patch(`/api/v1/financeiro/categorias/${id}/`, data);
    return response.data;
  },

  // Faturas
  getFaturas: async (params?: Record<string, any>): Promise<PaginatedResponse<Fatura>> => {
    const response = await api.get('/api/v1/financeiro/faturas/', { params });
    return response.data;
  },

  getFatura: async (id: number): Promise<Fatura> => {
    const response = await api.get(`/api/v1/financeiro/faturas/${id}/`);
    return response.data;
  },

  createFatura: async (data: Partial<Fatura>): Promise<Fatura> => {
    const response = await api.post('/api/v1/financeiro/faturas/', data);
    return response.data;
  },

  updateFatura: async (id: number, data: Partial<Fatura>): Promise<Fatura> => {
    const response = await api.put(`/api/v1/financeiro/faturas/${id}/`, data);
    return response.data;
  },

  deleteFatura: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/financeiro/faturas/${id}/`);
  },
};

// ===== ESTOQUE API =====
export const estoqueApi = {
  // Materiais
  getMateriais: async (params?: Record<string, any>): Promise<PaginatedResponse<Material>> => {
    const response = await api.get('/api/v1/estoque/materiais/', { params });
    return response.data;
  },

  getMaterial: async (id: number): Promise<Material> => {
    const response = await api.get(`/api/v1/estoque/materiais/${id}/`);
    return response.data;
  },

  createMaterial: async (data: MaterialFormData): Promise<Material> => {
    const response = await api.post('/api/v1/estoque/materiais/', data);
    return response.data;
  },

  updateMaterial: async (id: number, data: Partial<MaterialFormData>): Promise<Material> => {
    const response = await api.put(`/api/v1/estoque/materiais/${id}/`, data);
    return response.data;
  },

  deleteMaterial: async (id: number): Promise<void> => {
    await api.delete(`/api/v1/estoque/materiais/${id}/`);
  },

  getMateriaisBaixoEstoque: async (): Promise<Material[]> => {
    const response = await api.get('/api/v1/estoque/materiais/baixo_estoque/');
    return response.data;
  },

  getRelatorioEstoque: async (): Promise<any> => {
    const response = await api.get('/api/v1/estoque/materiais/relatorio/');
    return response.data;
  },

  // Movimentações
  getMovimentacoes: async (params?: Record<string, any>): Promise<PaginatedResponse<MovimentacaoEstoque>> => {
    const response = await api.get('/api/v1/estoque/movimentacoes/', { params });
    return response.data;
  },

  getMovimentacao: async (id: number): Promise<MovimentacaoEstoque> => {
    const response = await api.get(`/api/v1/estoque/movimentacoes/${id}/`);
    return response.data;
  },

  createMovimentacao: async (data: MovimentacaoEstoqueFormData): Promise<MovimentacaoEstoque> => {
    const response = await api.post('/api/v1/estoque/movimentacoes/', data);
    return response.data;
  },

  cancelarMovimentacao: async (id: number): Promise<MovimentacaoEstoque> => {
    const response = await api.post(`/api/v1/estoque/movimentacoes/${id}/cancelar/`);
    return response.data;
  },

  // Notas Fiscais
  getNotasFiscais: async (params?: Record<string, any>): Promise<PaginatedResponse<NotaFiscal>> => {
    const response = await api.get('/api/v1/estoque/notas-fiscais/', { params });
    return response.data;
  },

  getNotaFiscal: async (id: number): Promise<NotaFiscal> => {
    const response = await api.get(`/api/v1/estoque/notas-fiscais/${id}/`);
    return response.data;
  },

  importarNFe: async (xmlFile: File): Promise<NotaFiscal> => {
    const formData = new FormData();
    formData.append('xml_file', xmlFile);
    const response = await api.post('/api/v1/estoque/notas-fiscais/importar_xml/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  lancarNotaFiscal: async (id: number): Promise<{ message: string; itens_lancados: number }> => {
    const response = await api.post(`/api/v1/estoque/notas-fiscais/${id}/lancar_estoque/`);
    return response.data;
  },

  conferirNotaFiscal: async (id: number, itens?: Array<{item_id: number, material_id: number | null}>): Promise<NotaFiscal> => {
    const response = await api.post(`/api/v1/estoque/notas-fiscais/${id}/conferir/`, {
      itens: itens || []
    });
    return response.data;
  },

  lancarEstoqueNFe: async (id: number): Promise<NotaFiscal> => {
    const response = await api.post(`/api/v1/estoque/notas-fiscais/${id}/lancar_estoque/`);
    return response.data;
  },

  getItensNotaFiscal: async (notaFiscalId: number): Promise<ItemNotaFiscal[]> => {
    const response = await api.get(`/api/v1/estoque/notas-fiscais/${notaFiscalId}/itens/`);
    return response.data;
  },

  // Dashboard Estoque
  getDashboardEstoque: async (): Promise<EstoqueDashboardData> => {
    const response = await api.get('/api/v1/estoque/dashboard/');
    return response.data;
  },
};

export default api;